import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import Space from '@/components/Space';

const STRETCH_AMOUNT = 3;

function StretchySpringVsBezierDemo() {
  return (
    <Row>
      <Col>
        <Heading>Bézier easing</Heading>
        <Arrow easing="bezier" />
      </Col>
      <Col>
        <Heading>Spring easing</Heading>
        <Arrow easing="spring" />
      </Col>
    </Row>
  );
}

function Arrow({ easing }: { easing: 'bezier' | 'spring' }) {
  const [isHovering, setIsHovering] = React.useState(false);

  const lineProps = {
    x2: LINE_EXTENDS_TO[isHovering ? 'booped' : 'default'],
  };
  const polyLineProps = {
    points: POINTS[isHovering ? 'squeezed' : 'default'],
  };

  const transition =
    easing === 'bezier'
      ? { duration: 0.4, ease: [0.35, 0, 0.35, 1] }
      : { type: 'spring', stiffness: 300, damping: 12 };

  return (
    <ArrowBtn
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={() => setIsHovering(true)}
      onTouchEnd={() => setIsHovering(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(true);
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          setIsHovering(false);
        }
      }}
    >
      <Svg
        data-easing={easing}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <motion.line
          x1="5"
          y1="12"
          y2="12"
          animate={lineProps}
          transition={transition}
        />
        <motion.polyline
          animate={polyLineProps}
          transition={transition}
        />
      </Svg>
    </ArrowBtn>
  );
}

const POINTS = {
  default: '12 5 19 12 12 19',
  squeezed: `${12 + STRETCH_AMOUNT} 7 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 17`,
  regular: `${12 + STRETCH_AMOUNT} 5 ${19 + STRETCH_AMOUNT} 12 ${
    12 + STRETCH_AMOUNT
  } 19`,
};
const LINE_EXTENDS_TO = {
  default: 18,
  booped: 18 + STRETCH_AMOUNT,
};

const Row = styled(Space)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 32px;
  gap: 32px;
  border: var(--page-border);
  border-radius: 8px;
  margin-bottom: 64px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Heading = styled.p`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  text-align: center;
  margin-bottom: 0;
`;

const ArrowBtn = styled.button`
  padding: 12px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const Svg = styled.svg`
  display: block;
  transition: opacity 250ms;
  width: 128px;
  height: 128px;
  overflow: visible;
  fill: none;
  stroke: white;
  stroke-width: 2px;
  stroke-linecap: round;
  stroke-linejoin: round;

  &[data-easing='bezier'] line,
  &[data-easing='bezier'] polyline {
    transition: all 250ms;
  }
`;

export default StretchySpringVsBezierDemo;
